import { Link, useLocation } from 'react-router-dom';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

import { NAVIGATION } from '../../config/navigation';

/**
 * Breadcrumbs derived from the URL and the navigation config.
 *
 * Deriving rather than declaring means a page cannot forget to set them, and
 * they cannot drift from the sidebar. A detail route (/brands/12) shows the
 * section, the module, then a trailing crumb the page can label via
 * `<PageHeader breadcrumbLabel>`; absent that, the raw segment is shown.
 */
export function AdminBreadcrumbs({ trailingLabel }: { trailingLabel?: string }) {
  const { pathname } = useLocation();

  const section = NAVIGATION.find((group) =>
    group.items.some((item) => pathname === item.path || pathname.startsWith(`${item.path}/`)),
  );

  const navItem = section?.items.find(
    (item) => pathname === item.path || pathname.startsWith(`${item.path}/`),
  );

  if (!section || !navItem) return null;

  const isDetailRoute = pathname !== navItem.path;
  const detailSegment = isDetailRoute ? pathname.slice(navItem.path.length + 1) : null;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-xs">
        {/* The section is a grouping label, not a route, so it is plain text. */}
        <BreadcrumbItem className="text-muted-foreground hidden sm:inline-flex">
          {section.title}
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden sm:block" />

        <BreadcrumbItem>
          {isDetailRoute ? (
            <BreadcrumbLink asChild>
              <Link to={navItem.path}>{navItem.title}</Link>
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage className="font-medium">{navItem.title}</BreadcrumbPage>
          )}
        </BreadcrumbItem>

        {isDetailRoute && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium">
                {trailingLabel ?? detailSegment}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
